/**
 * feat-301-server-multiple-roles — server role set (spec D1/D2, ADR 0001 §6.1).
 *
 * A server holds a SET of tenant roles (role_keys), exactly one of which is the
 * PRIMARY (`default_role`). This module owns:
 *   - `serverRolesFromApi`: read-path normalisation (fallback for pre-slice-2 APIs)
 *   - `ServerRolesCell`: the server list's Role column (design L1–L4)
 *
 * The form control lives in `ServerRolesField.tsx`.
 */
import React from "react";
import type { ApiError } from "../../utils/api";

/** Copy for the server-roles surfaces (design/NOTES.md §3). */
export const SERVER_ROLES_COPY = {
  legend: "Roles",
  primary: "Primary",
  helper:
    "Primary is used whenever the app doesn't send a role. Servers with 2 or more roles choose one each time they start a shift.",
  noneSelected: "No role. Shifts use the default prompt.",
  primaryMoved: (displayName: string) => `Primary moved to ${displayName}.`,
  tenantHasNoRoles: "This tenant has no roles set up yet, so this server can't be given one.",
  loading: "Loading roles…",
  loadFailed: (keys: string[]) =>
    `Couldn't load this tenant's roles. Saving won't change this server's roles (${
      keys.length ? keys.join(", ") : "none"
    }).`,
  cellPrimarySr: " (primary)",
  cellTitle: (name: string) => `Primary: ${name}`,
} as const;

/**
 * The server's role set from an API server row, primary first.
 *
 * Slice-2 APIs send `roles` (primary first, ADR §6.1). Older APIs send only
 * `default_role`; then the set is `[default_role]` (or `[]`). The API's order
 * is kept — never re-sorted here (design L4) — except that the primary is
 * guaranteed first if, for any reason, it is present but not leading.
 */
export function serverRolesFromApi(roles: unknown, defaultRole: unknown): string[] {
  const primary = typeof defaultRole === "string" ? defaultRole : "";
  if (Array.isArray(roles)) {
    const keys = roles.filter((r): r is string => typeof r === "string" && r !== "");
    const idx = primary ? keys.indexOf(primary) : -1;
    if (idx > 0) {
      return [primary, ...keys.slice(0, idx), ...keys.slice(idx + 1)];
    }
    return keys;
  }
  return primary ? [primary] : [];
}

/** A tenant's role_key → display_name lookup (feat-308). */
export type RoleNameLookup = Readonly<Record<string, string>>;

/**
 * The display name for a role_key, or the raw key when the lookup has no
 * (non-blank) entry for it (feat-308 spec A3 — never blank).
 */
export function roleDisplayName(key: string, roleNames?: RoleNameLookup | null): string {
  const name = roleNames && Object.prototype.hasOwnProperty.call(roleNames, key)
    ? roleNames[key]
    : undefined;
  return typeof name === "string" && name.trim() !== "" ? name : key;
}

/**
 * Server list Role cell (design panel L): roles primary first.
 *
 * feat-308: with `roleNames`, each role_key renders as its tenant display
 * name; a key missing from the lookup (or no lookup at all) renders raw.
 */
export const ServerRolesCell: React.FC<{
  roles: string[];
  roleNames?: RoleNameLookup | null;
}> = ({ roles, roleNames }) => {
  if (roles.length === 0) {
    return (
      <td className="py-2 px-3 text-sm" data-testid="server-roles-cell">
        {"—"}
      </td>
    );
  }
  const [primary, ...rest] = roles.map((k) => roleDisplayName(k, roleNames));
  return (
    <td
      className="py-2 px-3 text-sm max-w-[16rem]"
      title={SERVER_ROLES_COPY.cellTitle(primary)}
      data-testid="server-roles-cell"
    >
      <span className="break-words">{primary}</span>
      <span className="sr-only">{SERVER_ROLES_COPY.cellPrimarySr}</span>
      {rest.length > 0 && (
        <span className="text-gray-500 break-words">{`, ${rest.join(", ")}`}</span>
      )}
    </td>
  );
};

/**
 * The modal banner text for a failed server create/update.
 *
 * The server CRUD's role 422s carry `detail` as an OBJECT
 * (`{error: unknown_role | primary_not_in_roles | primary_required, message}`,
 * ADR 0001 §6.1), unlike FastAPI's list-shaped validation 422s. `fetchApi`
 * leaves `.message` as `[object Object]` for those (by contract, feat-243), so
 * read `detail.message` off the kept body here. Every other error keeps
 * today's `err.message`.
 *
 * Duck-typed on `.body` rather than `instanceof ApiError`: several test suites
 * mock `utils/api` with only `fetchApi`, where `ApiError` is undefined and an
 * `instanceof` would throw inside the save handler's catch.
 */
export function serverSaveErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "body" in err) {
    const body = (err as ApiError).body as { detail?: unknown } | null | undefined;
    const detail = body && typeof body === "object" ? body.detail : undefined;
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string" && message.trim()) return message;
    }
  }
  return err instanceof Error ? err.message : fallback;
}
